import { useAsyncDataRequestStatus } from '@/composables/utils/useAsyncDataRequestStatus';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { useAsyncFetchWithUniqueKeyFromPublicWabunkaApi } from '@/wabunka-src/common/composables/query/useAsyncFetchWithUniqueKeyFromPublicWabunkaApi';
import {
  buildWabunkaExperienceMapPinListViewModel,
  type WabunkaExperienceMapPinJson,
  type WabunkaExperienceMapPinResponseJson,
} from '@/wabunka-src/common/features/Experience/models/wabunkaExperienceMapPin';

/** 地図の描画範囲（南西・北東コーナー） */
export interface MapPinBoundingBox {
  southWestLatitude: number;
  southWestLongitude: number;
  northEastLatitude: number;
  northEastLongitude: number;
}

interface WabunkaExperienceMapPinQuery {
  experienceEventStartAtDates?: string;
  destinationTagWebPaths?: string;
  themeTagWebPaths?: string;
  preferenceTagWebPaths?: string;
  slugs?: string;
  sort?: string;
  currency?: string;
  // API はドット区切りのネストしたパラメータ形式を期待するため、クォートで囲んだ文字列キーを使用
  'experienceEventStartTimeRange.from'?: string;
  'experienceEventStartTimeRange.to'?: string;
  'boundingBox.southWestLatitude'?: string;
  'boundingBox.southWestLongitude'?: string;
  'boundingBox.northEastLatitude'?: string;
  'boundingBox.northEastLongitude'?: string;
}

/**
 * ピン配信レスポンス（proto JSON）を最低限バリデーションしてから型を付ける。
 * ts_proto の `fromJSON` に置き換えるまでの暫定コンバーター。
 * `items` が配列でない／オブジェクトでないレスポンスは空レスポンスとして扱う（描画は 0 件になる）。
 */
const toMapPinResponseJson = (json: unknown): WabunkaExperienceMapPinResponseJson => {
  if (typeof json !== 'object' || json === null) return {};
  const obj = json as Record<string, unknown>;
  if (obj.items !== undefined && !Array.isArray(obj.items)) return {};
  if (Array.isArray(obj.items)) {
    // 各要素が非オブジェクト（null / プリミティブ）だとビューモデル変換（pin.latitude 参照）で例外になるため除外する
    return {
      ...obj,
      items: obj.items.filter((item): item is WabunkaExperienceMapPinJson => typeof item === 'object' && item !== null),
    } as WabunkaExperienceMapPinResponseJson;
  }
  return obj as WabunkaExperienceMapPinResponseJson;
};

/**
 * 一覧マップビュー用のマップピン軽量配信（`GET /experiences/mapPins`）を取得する Composable。
 * Page でのみ使用可能。
 *
 * 既存の体験検索と同じフィルタ（エリア・テーマ・日付・時間帯・slugs・currency）に加え、
 * 任意で地図の描画範囲（boundingBox）で絞り込む。boundingBox はリアクティブに変化するため、
 * 地図の移動に追従して自動で再取得される。
 *
 * マップは client でのみ描画するため、取得も client 側で行う（server: false）。
 * マップは補助機能のため、エンドポイント未提供・失敗時もページを壊さないよう throwOnError: false で
 * graceful degradation（ピン 0 件表示）する（ADR-108）。
 */
export const useAsyncWabunkaExperienceMapPins = async (query: {
  experienceEventStartAtDates: Ref<string>;
  destinations: Ref<string>;
  themes: Ref<string>;
  preferences?: Ref<string>;
  slugs?: Ref<string[]>;
  experienceEventStartTimeRangeFrom?: Ref<string>;
  experienceEventStartTimeRangeTo?: Ref<string>;
  /** API のソートキー。おすすめ順は backend の既定値のため undefined を渡す */
  sort?: Ref<string | undefined>;
  boundingBox: Ref<MapPinBoundingBox | null>;
}) => {
  const path = computed(() => `/experiences/mapPins`);
  const { selectedCurrency } = useCurrency();

  const queryRef = computed<WabunkaExperienceMapPinQuery>(() => {
    const queryObj: WabunkaExperienceMapPinQuery = {
      currency: selectedCurrency.value !== 'JPY' ? selectedCurrency.value : undefined,
    };

    if (query.experienceEventStartAtDates?.value) {
      queryObj.experienceEventStartAtDates = query.experienceEventStartAtDates.value;
    }
    if (query.destinations?.value) {
      queryObj.destinationTagWebPaths = query.destinations.value;
    }
    if (query.themes?.value) {
      queryObj.themeTagWebPaths = query.themes.value;
    }
    if (query.preferences?.value) {
      queryObj.preferenceTagWebPaths = query.preferences.value;
    }
    if (query.slugs?.value && query.slugs.value.length > 0) {
      queryObj.slugs = query.slugs.value.join(',');
    }
    if (query.experienceEventStartTimeRangeFrom?.value) {
      queryObj['experienceEventStartTimeRange.from'] = query.experienceEventStartTimeRangeFrom.value;
    }
    if (query.experienceEventStartTimeRangeTo?.value) {
      queryObj['experienceEventStartTimeRange.to'] = query.experienceEventStartTimeRangeTo.value;
    }
    if (query.sort?.value) {
      queryObj.sort = query.sort.value;
    }

    const bbox = query.boundingBox.value;
    if (bbox) {
      queryObj['boundingBox.southWestLatitude'] = String(bbox.southWestLatitude);
      queryObj['boundingBox.southWestLongitude'] = String(bbox.southWestLongitude);
      queryObj['boundingBox.northEastLatitude'] = String(bbox.northEastLatitude);
      queryObj['boundingBox.northEastLongitude'] = String(bbox.northEastLongitude);
    }

    return queryObj;
  });

  const { data, refresh, status } = await useAsyncFetchWithUniqueKeyFromPublicWabunkaApi({
    path,
    query: queryRef,
    options: { server: false, immediate: true },
    converter: toMapPinResponseJson,
    throwOnError: false,
  });

  const pins = computed(() => buildWabunkaExperienceMapPinListViewModel(data.value ?? undefined));

  return { pins, refresh, ...useAsyncDataRequestStatus(status) };
};
